import { SubTrayItemProductDetails } from 'lib/services/api/product-service/receiveItems/types'

/**
 * Gets the latest tray image from a tray's product details.
 * Handles both old and new data structures for backward compatibility.
 *
 * @param trayDetails The tray product details object
 * @param excludedRoles Array of roles to exclude from consideration (e.g., ['nurse', 'rep'])
 * @returns The URL of the latest tray image or undefined if no tray details provided
 */
export const getLatestTrayImage = (
  trayDetails?: SubTrayItemProductDetails,
  excludedRoles: Array<'rep' | 'spd' | 'nurse'> = []
): string | undefined => {
  if (!trayDetails) return undefined

  // If using the new data structure with image history
  if (trayDetails.trayImageHistory) {
    // Get all available latest images
    const latestImages = trayDetails.trayImageHistory.latest

    // Find the most recent image by comparing timestamps, excluding specified roles
    const allLatestImages = [
      // Only include images from roles that aren't excluded
      !excludedRoles.includes('rep') ? latestImages.rep : undefined,
      !excludedRoles.includes('spd') ? latestImages.spd : undefined,
      !excludedRoles.includes('nurse') ? latestImages.nurse : undefined,
    ].filter(Boolean) as Array<typeof latestImages.rep>

    if (allLatestImages.length > 0) {
      // Sort by timestamp (most recent first) and return the URL of the most recent image
      const mostRecent = allLatestImages.sort(
        (a, b) =>
          new Date(b!.timestamp).getTime() - new Date(a!.timestamp).getTime()
      )[0]
      return mostRecent?.imageUrl || trayDetails.trayImg
    }

    // If no images in the history, fall back to the old field
    return trayDetails.trayImg
  }

  // Fallback to the old trayImg field if trayImageHistory doesn't exist
  return trayDetails.trayImg
}
